import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

// Aviso de privacidad de la plataforma Mascotas Vet (mascotas.vet), conforme
// a la Ley Federal de Protección de Datos Personales en Posesión de los
// Particulares (LFPDPPP) y su Reglamento. Antes de publicar, reemplaza los
// Responsable: Mascotas Vet, S.A. de C.V., con domicilio en Guadalajara,
// Jalisco, México.

const ULTIMA_ACTUALIZACION = "28 de julio de 2026";

function Seccion({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="mt-8">
      <h2 className="font-display text-xl text-slate-800">{titulo}</h2>
      <div className="mt-2 space-y-3 text-sm leading-relaxed text-slate-600">{children}</div>
    </section>
  );
}

export default function AvisoPrivacidad() {
  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-100">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-5">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-icono.png" alt="Mascotas Vet" className="h-9 w-9" />
            <span className="font-display text-xl text-slate-800">Mascotas Vet</span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-blue-600">
            <ArrowLeft size={15} /> Volver al inicio
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 py-10">
        <h1 className="font-display text-3xl text-slate-800">Aviso de privacidad</h1>
        <p className="mt-2 text-sm text-slate-400">Última actualización: {ULTIMA_ACTUALIZACION}</p>

        <p className="mt-6 text-sm leading-relaxed text-slate-600">
          En Mascotas Vet nos tomamos en serio la protección de tus datos personales. Este aviso explica qué información
          recabamos a través del sitio mascotas.vet y de la plataforma (la "Plataforma"), para qué la usamos, con quién la
          compartimos y cómo puedes ejercer tus derechos, de conformidad con la Ley Federal de Protección de Datos
          Personales en Posesión de los Particulares y su Reglamento.
        </p>

        <Seccion titulo="1. Identidad y domicilio del responsable">
          <p>
            El responsable del tratamiento de tus datos personales es <strong>Mascotas Vet, S.A. de C.V.</strong>,
            sociedad mexicana con domicilio en <strong>Guadalajara, Jalisco, México</strong>. Puedes
            contactarnos para cualquier duda relacionada con este aviso o con tus datos personales al correo{" "}
            <a href="mailto:soporte@mascotas.vet" className="text-blue-600 hover:underline">soporte@mascotas.vet</a>.
          </p>
        </Seccion>

        <Seccion titulo="2. ¿Mascotas Vet es responsable o encargado de tus datos?">
          <p>
            Esto depende de quién eres:
          </p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              <strong>Si tienes o representas un negocio</strong> (veterinaria, pet shop, estética o negocio de hospedaje)
              que usa Mascotas Vet: para tus propios datos de registro, facturación y uso de la Plataforma, nosotros
              somos <strong>responsables</strong> de tu información conforme a este aviso.
            </li>
            <li>
              <strong>Si eres cliente de un negocio</strong> que usa Mascotas Vet (por ejemplo, el tutor de una mascota
              atendida en esa veterinaria): tus datos y los de tu mascota los captura y administra ese negocio, que es el{" "}
              <strong>responsable</strong> de esa información. Mascotas Vet actuamos únicamente como{" "}
              <strong>encargados</strong> — es decir, tratamos esos datos por cuenta e instrucción del negocio, solo para
              que la Plataforma funcione (expedientes, recordatorios, portal del cliente, facturación, etc.), y no los
              usamos para fines propios. Para ejercer tus derechos sobre esos datos, contacta directamente al negocio
              donde atienden a tu mascota; si no logras contactarlo, también puedes escribirnos y te ayudamos a
              canalizar tu solicitud.
            </li>
            <li>
              <strong>Si solo visitas mascotas.vet</strong> sin registrarte: aplican las secciones de este aviso
              relacionadas con datos de navegación.
            </li>
          </ul>
        </Seccion>

        <Seccion titulo="3. Datos personales que recabamos">
          <p><strong>De quienes registran un negocio o usan la Plataforma como parte de su equipo:</strong></p>
          <ul className="list-disc space-y-1 pl-5">
            <li>Datos de identificación y contacto: nombre, correo electrónico, teléfono.</li>
            <li>Credenciales de acceso (tu contraseña se guarda siempre cifrada, nunca en texto plano).</li>
            <li>
              Datos de facturación, solo si activas la función de facturación electrónica: RFC, razón social, régimen
              fiscal, domicilio fiscal, y tu Certificado de Sello Digital (CSD), el cual guardamos cifrado en nuestros
              servidores.
            </li>
            <li>Domicilio del negocio (para mostrarlo en el mapa de negocios y para geolocalización de servicio a domicilio).</li>
            <li>
              Datos de pago de tu suscripción: los procesa directamente nuestro proveedor de pagos (Stripe); Mascotas
              Vet no almacena números de tarjeta.
            </li>
          </ul>
          <p className="mt-3"><strong>De quienes visitan mascotas.vet:</strong></p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              Datos de navegación: páginas que visitas, botones en los que das clic y un identificador anónimo de
              visita que guardamos en tu navegador (no en cookies de terceros ni redes de publicidad), únicamente para
              entender qué partes del sitio se usan más y mejorar la Plataforma.
            </li>
            <li>Si llenas el formulario de "solicitar cuenta demo" o de contacto: nombre, correo, teléfono y el mensaje que escribas.</li>
          </ul>
        </Seccion>

        <Seccion titulo="4. Finalidades del tratamiento">
          <p><strong>Finalidades necesarias para el servicio (sin estas no podríamos operar tu cuenta):</strong></p>
          <ul className="list-disc space-y-1 pl-5">
            <li>Crear y administrar tu cuenta y la de tu negocio en la Plataforma.</li>
            <li>Procesar tu suscripción y, si aplica, generar tus facturas electrónicas (CFDI) ante el SAT a través de nuestro proveedor autorizado de certificación (PAC).</li>
            <li>Enviarte notificaciones operativas por correo o WhatsApp: acceso a tu cuenta nueva, recordatorios, avisos de pago, respuestas de soporte.</li>
            <li>Dar soporte técnico y atender tus dudas.</li>
            <li>Cumplir obligaciones legales y fiscales.</li>
          </ul>
          <p className="mt-3"><strong>Finalidades secundarias (puedes oponerte sin que esto afecte el servicio):</strong></p>
          <ul className="list-disc space-y-1 pl-5">
            <li>Análisis estadístico de uso del sitio y la Plataforma, para mejorar el producto.</li>
            <li>Comunicación de novedades, mejoras o promociones de Mascotas Vet.</li>
          </ul>
          <p>
            Si no deseas que tus datos se usen para las finalidades secundarias, escríbenos a{" "}
            <a href="mailto:soporte@mascotas.vet" className="text-blue-600 hover:underline">soporte@mascotas.vet</a> indicando tu solicitud.
          </p>
        </Seccion>

        <Seccion titulo="5. Con quién compartimos tu información (encargados y transferencias)">
          <p>
            Para poder ofrecerte el servicio, compartimos ciertos datos con proveedores que tratan información
            únicamente por nuestra instrucción y bajo acuerdos de confidencialidad, nunca para sus propios fines:
          </p>
          <ul className="list-disc space-y-1 pl-5">
            <li><strong>Stripe</strong> (procesamiento de pagos de tu suscripción).</li>
            <li><strong>Facturama</strong> (Proveedor Autorizado de Certificación, para timbrar tus CFDI ante el SAT, solo si usas facturación).</li>
            <li><strong>Meta (WhatsApp Business Platform)</strong> — o Twilio, según el caso — para enviarte notificaciones por WhatsApp que hayas solicitado.</li>
            <li><strong>Proveedor de correo transaccional</strong> (por ejemplo Brevo), para el envío de correos operativos.</li>
            <li><strong>Servicio de geocodificación</strong> (OpenStreetMap/Nominatim), para ubicar domicilios en el mapa.</li>
            <li><strong>Railway</strong> (proveedor de hospedaje en la nube donde viven nuestros servidores y base de datos).</li>
          </ul>
          <p>
            No vendemos tus datos personales ni los compartimos con fines de mercadotecnia de terceros ajenos a
            Mascotas Vet.
          </p>
        </Seccion>

        <Seccion titulo="6. Tecnologías de rastreo">
          <p>
            Usamos un identificador anónimo que guardamos en tu navegador (similar a una cookie propia, no de
            terceros) para contar visitas y entender qué secciones del sitio se usan más. No usamos este identificador
            para armar un perfil publicitario tuyo ni lo compartimos con redes de publicidad.
          </p>
        </Seccion>

        <Seccion titulo="7. Derechos ARCO">
          <p>
            Tienes derecho a Acceder a tus datos personales, Rectificarlos si son inexactos, Cancelarlos cuando
            consideres que no se requieren para alguna de las finalidades señaladas, y Oponerte al uso de tus datos
            para fines específicos (derechos ARCO). También puedes revocar el consentimiento que, en su caso, nos
            hayas otorgado.
          </p>
          <p>
            Para ejercer cualquiera de estos derechos, escríbenos a{" "}
            <a href="mailto:soporte@mascotas.vet" className="text-blue-600 hover:underline">soporte@mascotas.vet</a>{" "}
            indicando: (1) tu nombre y correo de contacto, (2) una descripción clara de los datos sobre los que
            quieres ejercer el derecho, y (3) cualquier documento que ayude a acreditar tu identidad. Te
            responderemos dentro de los plazos que marca la ley.
          </p>
        </Seccion>

        <Seccion titulo="8. Menores de edad">
          <p>
            La Plataforma está dirigida a negocios y a personas mayores de edad. No recabamos intencionalmente datos
            personales de menores de edad. Si detectamos que un menor nos proporcionó datos sin el consentimiento de
            su padre, madre o tutor, procederemos a eliminarlos.
          </p>
        </Seccion>

        <Seccion titulo="9. Cambios a este aviso de privacidad">
          <p>
            Podemos actualizar este aviso de privacidad, por ejemplo ante cambios en nuestros servicios o cambios
            legales. Publicaremos cualquier actualización en esta misma página, indicando la fecha de la última
            actualización en la parte superior.
          </p>
        </Seccion>

        <p className="mt-10 text-xs text-slate-400">
          
        </p>
      </main>
    </div>
  );
}
